import { useCallback, useEffect, useRef, useState } from "react";
import {
  Building2,
  Check,
  Copy,
  Download,
  Eye,
  GripVertical,
  History,
  MousePointer2,
  PencilLine,
  RefreshCw,
  Save,
  Sparkles,
  FileText,
  Stamp,
  Trash2,
  Upload,
  UserRound,
  UsersRound,
  X,
} from "lucide-react";
import * as pdfjsLib from "pdfjs-dist";
import PdfWorker from "pdfjs-dist/build/pdf.worker.min.mjs?worker&inline";
import {
  createManagedTemplate,
  createTemplateDraft,
  createTemplateVersion,
  fetchTemplateVersionFile,
  getManagedTemplateBySource,
  getTemplateVersions,
  getTemplateVersion,
  importSourceTemplate,
  publishTemplateVersion,
  saveTemplateVersionContent,
  saveTemplateVersionSettings,
  saveVersionSignatureConfig,
  templateDownloadUrl,
  updateTemplateClassification,
} from "../lib/api";
import { importEditableSourceTemplate } from "../lib/importEditableSourceTemplate";
import { classNames, formatBytes, formatDate } from "../lib/format";
import { selectTemplateOpeningVersion } from "../lib/templateVersionSelection";
import { LoadingState } from "./Shared";
import TemplateContentEditor from "./TemplateContentEditor";

// 中文维护注释：worker 内联进三端构建，避免生产静态服务器漏发独立 mjs 后退化为 fake worker 并读取失败。
pdfjsLib.GlobalWorkerOptions.workerPort = new PdfWorker();

const FILE_ACCEPT = ".doc,.docx,.pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf";
const DEFAULT_FIELD_SIZE = {
  "company-seal": { width: 14, height: 14 },
  "personal-signature": { width: 18, height: 7 },
};

function cleanName(fileName = "") {
  return String(fileName).replace(/\.(docx?|pdf|html?)$/i, "").replace(/_\d{12,}$/g, "").trim();
}

function createRole(type, index = 0) {
  const company = type === "company";
  return {
    id: crypto.randomUUID(),
    type,
    action: company ? "company-seal" : "personal-signature",
    label: company ? "企业方" : type === "employee" ? "员工方" : "其他签署方",
    variableBinding: company ? "companyName" : type === "employee" ? "staffName" : "",
    order: index + 1,
  };
}

function normalizedConfig(version) {
  const config = version?.signatureConfig || {};
  return {
    schemaVersion: 1,
    sourceSha256: version?.renderedPdfSha256 || version?.sourceFileSha256 || "",
    pageCount: Number(config.pageCount) || Number(version?.pageCount) || 1,
    roles: Array.isArray(config.roles) ? config.roles : [],
    fields: Array.isArray(config.fields) ? config.fields : [],
  };
}

function PdfPage({ pdf, pageNumber, fields, roles, selectedFieldId, placementRoleId, onFieldSelect, onFieldChange, onFieldDelete, onDropRole, onPlaceRole, readOnly = false }) {
  const canvasRef = useRef(null);
  const layerRef = useRef(null);
  const [aspectRatio, setAspectRatio] = useState("210 / 297");

  useEffect(() => {
    let cancelled = false;
    let renderTask;
    pdf.getPage(pageNumber).then((page) => {
      if (cancelled) return;
      const viewport = page.getViewport({ scale: 1.45 });
      setAspectRatio(`${viewport.width} / ${viewport.height}`);
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ratio = window.devicePixelRatio || 1;
      canvas.width = Math.floor(viewport.width * ratio);
      canvas.height = Math.floor(viewport.height * ratio);
      renderTask = page.render({
        canvasContext: canvas.getContext("2d"),
        viewport,
        transform: ratio === 1 ? null : [ratio, 0, 0, ratio, 0, 0],
      });
      return renderTask.promise;
    }).catch(() => {});
    return () => {
      cancelled = true;
      renderTask?.cancel?.();
    };
  }, [pdf, pageNumber]);

  const positionFromPointer = useCallback((event, field, mode = "move", origin = field) => {
    const rect = layerRef.current?.getBoundingClientRect();
    if (!rect?.width || !rect?.height) return;
    const dx = ((event.clientX - origin.pointerX) / rect.width) * 100;
    const dy = ((event.clientY - origin.pointerY) / rect.height) * 100;
    if (mode === "resize") {
      const width = Math.max(5, Math.min(40, origin.width + dx));
      const height = field.action === "company-seal"
        ? Math.max(5, Math.min(28, width * rect.width / rect.height))
        : Math.max(3.5, Math.min(18, origin.height + dy));
      onFieldChange(field.id, {
        width,
        height,
        x: Math.min(origin.x, 100 - width),
        y: Math.min(origin.y, 100 - height),
      });
      return;
    }
    onFieldChange(field.id, {
      x: Math.max(0, Math.min(100 - field.width, origin.x + dx)),
      y: Math.max(0, Math.min(100 - field.height, origin.y + dy)),
    });
  }, [onFieldChange]);

  function startPointer(event, field, mode) {
    if (readOnly) return;
    event.preventDefault();
    event.stopPropagation();
    onFieldSelect(field.id);
    const origin = { ...field, pointerX: event.clientX, pointerY: event.clientY };
    const move = (moveEvent) => positionFromPointer(moveEvent, field, mode, origin);
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }

  function drop(event) {
    event.preventDefault();
    if (readOnly) return;
    const roleId = event.dataTransfer.getData("application/x-signature-role");
    const rect = layerRef.current?.getBoundingClientRect();
    if (!roleId || !rect) return;
    onDropRole(roleId, pageNumber, ((event.clientX - rect.left) / rect.width) * 100, ((event.clientY - rect.top) / rect.height) * 100);
  }

  function place(event) {
    if (readOnly || !placementRoleId || event.target !== event.currentTarget) return;
    const rect = layerRef.current?.getBoundingClientRect();
    if (!rect) return;
    onPlaceRole(placementRoleId, pageNumber, ((event.clientX - rect.left) / rect.width) * 100, ((event.clientY - rect.top) / rect.height) * 100);
  }

  return (
    <section className="signature-pdf-page" style={{ aspectRatio }} aria-label={`PDF 第 ${pageNumber} 页`}>
      <canvas ref={canvasRef} />
      <div
        className={classNames("signature-field-layer", placementRoleId && !readOnly && "is-placing")}
        ref={layerRef}
        onDragOver={(event) => { if (!readOnly) event.preventDefault(); }}
        onDrop={drop}
        onClick={place}
      >
        {fields.map((field) => {
          const role = roles.find((item) => item.id === field.roleId);
          const editableField = field.action ? field : { ...field, action: role?.action };
          return (
            <div
              className={classNames(
                "signature-field-marker",
                editableField.action === "company-seal" && "is-company-seal",
                field.suggestion && "is-suggestion",
                selectedFieldId === field.id && "is-selected",
              )}
              key={field.id}
              style={{ left: `${field.x}%`, top: `${field.y}%`, width: `${field.width}%`, height: `${field.height}%` }}
              onPointerDown={(event) => startPointer(event, editableField, "move")}
              aria-label={`${role?.label || "签署方"}，第 ${pageNumber} 页签章位置`}
            >
              <span>{role?.label || "签署方"}</span>
              {!readOnly ? <button type="button" className="field-delete" title="删除签章位置" aria-label="删除签章位置" onPointerDown={(event) => { event.preventDefault(); event.stopPropagation(); }} onClick={(event) => { event.stopPropagation(); onFieldDelete(field.id); }}><Trash2 size={12} /></button> : null}
              {!readOnly ? <button type="button" className="field-resize" title="调整大小" aria-label="调整签章位置大小" onPointerDown={(event) => startPointer(event, editableField, "resize")}><GripVertical size={12} /></button> : null}
            </div>
          );
        })}
      </div>
      <span className="pdf-page-number">{pageNumber} / {pdf.numPages}</span>
    </section>
  );
}

export default function TemplateSignatureWorkbench({ file, catalog, onClose, onSaved, onClassificationSaved }) {
  const inputRef = useRef(null);
  const contentEditorRef = useRef(null);
  const [managed, setManaged] = useState(null);
  const [version, setVersion] = useState(null);
  const [config, setConfig] = useState(null);
  const [pdf, setPdf] = useState(null);
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");
  const [selectedFieldId, setSelectedFieldId] = useState("");
  const [placementRoleId, setPlacementRoleId] = useState("");
  const [configDirty, setConfigDirty] = useState(false);
  const [busy, setBusy] = useState("");
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("signature");
  const [contentView, setContentView] = useState("preview");
  const [contentDirty, setContentDirty] = useState(false);
  const [contentPageCount, setContentPageCount] = useState(0);
  const [classification, setClassification] = useState({
    moduleKey: file?.moduleKey || "",
    submoduleKey: file?.submoduleKey || "",
  });

  const classificationModule = catalog.modules.find((item) => item.moduleKey === classification.moduleKey);
  const classificationSubmodule = classificationModule?.submodules?.find(
    (item) => item.submoduleKey === classification.submoduleKey,
  );
  const savedModuleKey = managed?.moduleKey || file?.moduleKey || "";
  const savedSubmoduleKey = managed?.submoduleKey || file?.submoduleKey || "";
  const classificationDirty = classification.moduleKey !== savedModuleKey
    || classification.submoduleKey !== savedSubmoduleKey;

  const loadPdf = useCallback(async (nextVersion) => {
    const blob = await fetchTemplateVersionFile(nextVersion.id, "rendered-pdf");
    const bytes = await blob.arrayBuffer();
    const task = pdfjsLib.getDocument({ data: bytes });
    const nextPdf = await task.promise;
    setPdf((current) => {
      current?.destroy?.();
      return nextPdf;
    });
  }, []);

  const activateVersion = useCallback(async (nextManaged, nextVersion) => {
    // 切换版本时清空上一版的选中项和未保存标记，避免临时状态串到新 PDF。
    setManaged(nextManaged);
    setVersion(nextVersion);
    setConfig(normalizedConfig(nextVersion));
    setSelectedFieldId("");
    setPlacementRoleId("");
    setConfigDirty(false);
    setContentDirty(false);
    setContentPageCount(0);
    setContentView("preview");
    setActiveTab(nextVersion.editorFormat === "fixed" ? "signature" : "content");
    await loadPdf(nextVersion);
    setStatus("ready");
  }, [loadPdf]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setStatus("loading");
      try {
        if (file.pendingUpload) {
          const created = await createManagedTemplate({
            templateName: file.displayName || cleanName(file.fileName),
            sourceDocumentId: file.sourceDocumentId || file.id,
            moduleKey: file.moduleKey,
            moduleName: file.moduleName,
            submoduleKey: file.submoduleKey,
            submoduleName: file.submoduleName,
            templateType: "system",
            requiresElectronicSignature: file.requiresElectronicSignature !== false,
          }, file.pendingUpload);
          const details = await getTemplateVersion(created.id);
          if (!cancelled) await activateVersion({ id: created.templateId, versions: [details] }, details);
          return;
        }
        if ((file.databaseSource || file.sourceRecordId || file.filePath) && !file.managedTemplateId) {
          // 源库模板首次打开时自动纳管为草稿，避免停留在静态提示页。
          const imported = await importEditableSourceTemplate(file);
          const payload = { id: imported.templateId, versions: (await getTemplateVersions(imported.templateId)).versions || [] };
          const selected = selectTemplateOpeningVersion(payload);
          const details = selected.current?.id ? await getTemplateVersion(selected.current.id) : imported;
          if (!cancelled) await activateVersion(payload, details);
          onSaved?.(imported.templateId);
          return;
        }
        let payload;
        if (file.managedTemplateId) {
          payload = { id: file.managedTemplateId, versions: (await getTemplateVersions(file.managedTemplateId)).versions || [] };
        } else {
          try {
            payload = await getManagedTemplateBySource(file.id);
          } catch (error) {
            if (error.status === 404) {
              setStatus("unmanaged");
              return;
            }
            throw error;
          }
        }
        if (cancelled) return;
        const versions = payload.versions || [];
        const selected = selectTemplateOpeningVersion(payload);
        const preferredVersion = file.preferredVersionId
          ? versions.find((item) => item.id === file.preferredVersionId) || { id: file.preferredVersionId }
          : null;
        const current = preferredVersion || selected.current;
        if (!current) throw new Error("该模板没有可编辑版本");
        const details = current.signatureConfig === undefined
          ? await getTemplateVersion(current.id)
          : current;
        if (!cancelled) await activateVersion({ ...payload, publishedVersion: selected.published }, details);
      } catch (error) {
        if (error.status === 404 || error.message?.includes("尚未纳入")) {
          setStatus("unmanaged");
        } else {
          setMessage(error.message || "模板读取失败");
          setStatus("error");
        }
      }
    }
    load();
    return () => { cancelled = true; };
  }, [activateVersion, file.databaseSource, file.id, file.managedTemplateId, file.pendingUpload, file.preferredVersionId, file.requiresElectronicSignature, file.sourceDocumentId, file.sourceRecordId]);

  useEffect(() => () => pdf?.destroy?.(), [pdf]);

  async function uploadVersion(uploadFile) {
    if (!uploadFile) return;
    if (uploadFile.size > 12 * 1024 * 1024) {
      setMessage("文件不能超过 12 MB");
      return;
    }
    setBusy("upload");
    setMessage("");
    try {
      let nextVersion;
      let nextManaged = managed;
      if (status === "unmanaged") {
        nextVersion = await createManagedTemplate({
          templateName: file.displayName || cleanName(file.fileName),
          sourceDocumentId: file.id,
          moduleKey: file.moduleKey,
          moduleName: file.moduleName,
          submoduleKey: file.submoduleKey,
          submoduleName: file.submoduleName,
          templateType: "system",
          requiresElectronicSignature: file.requiresElectronicSignature !== false,
        }, uploadFile);
        nextManaged = file.id?.startsWith("new-")
          ? { id: nextVersion.templateId, versions: [nextVersion] }
          : await getManagedTemplateBySource(file.id);
      } else {
        nextVersion = await createTemplateVersion(managed.id, uploadFile, version?.requiresElectronicSignature ?? true);
        nextManaged = file.managedTemplateId || file.id?.startsWith("new-")
          ? { id: managed.id, versions: (await getTemplateVersions(managed.id)).versions || [] }
          : await getManagedTemplateBySource(file.id);
      }
      await activateVersion(nextManaged, nextVersion);
      setMessage("新草稿版本已创建，旧签章坐标未沿用，请重新确认");
    } catch (error) {
      setMessage(error.message || "上传失败");
    } finally {
      setBusy("");
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  function changeClassificationModule(moduleKey) {
    const nextModule = catalog.modules.find((item) => item.moduleKey === moduleKey);
    setClassification({
      moduleKey,
      submoduleKey: nextModule?.submodules?.[0]?.submoduleKey || "",
    });
  }

  async function saveClassification() {
    if (!classificationDirty || !classificationModule || !classificationSubmodule) return;
    setBusy("classification");
    setMessage("");
    let updated;
    try {
      updated = await updateTemplateClassification(managed.id, {
        moduleKey: classificationModule.moduleKey,
        moduleName: classificationModule.moduleName,
        submoduleKey: classificationSubmodule.submoduleKey,
        submoduleName: classificationSubmodule.submoduleName,
      });
      setManaged((current) => ({ ...current, ...updated }));
      setMessage(`归类已保存：${classificationModule.moduleName} / ${classificationSubmodule.submoduleName}`);
    } catch (error) {
      setMessage(error.message || "模板归类保存失败");
      return;
    } finally {
      setBusy("");
    }
    try {
      await onClassificationSaved?.(updated);
    } catch {
      setMessage("归类已保存，目录列表暂时未刷新，重新进入模板资产后即可看到最新位置");
    }
  }

  async function createDraftFromPublished() {
    if (version?.status !== "published") return;
    setBusy("draft");
    setMessage("正在基于当前正文创建新草稿并刷新版式 PDF");
    try {
      const draft = await createTemplateDraft(managed.id);
      const content = contentEditorRef.current?.getContent() || {
        documentJson: version.documentJson,
        editableHtml: version.editableHtml || "",
      };
      const saved = content?.editableHtml
        ? await saveTemplateVersionContent(draft.id, {
            ...content,
            pageCount: Math.max(1, Number(contentPageCount) || Number(version.pageCount) || 1),
            lockVersion: Number(draft.lockVersion || 0),
          })
        : draft;
      const versions = (await getTemplateVersions(managed.id)).versions || [];
      await activateVersion({ ...managed, versions }, saved);
      setMessage(`已基于发布版本创建草稿 V${saved.versionNo}，版式 PDF 已刷新`);
      onSaved?.(managed.id);
    } catch (error) {
      setMessage(error.message || "新草稿创建失败");
    } finally {
      setBusy("");
    }
  }

  async function openVersion(versionSummary) {
    if (!versionSummary?.id || versionSummary.id === version?.id) return;
    setBusy("history");
    setMessage("");
    try {
      const details = await getTemplateVersion(versionSummary.id);
      await activateVersion(managed, details);
      setVersionHistoryOpen(false);
      setMessage(`已切换到 V${details.versionNo} · ${details.status === "published" ? "已发布" : "草稿"}`);
    } catch (error) {
      setMessage(error.message || "版本读取失败");
    } finally {
      setBusy("");
    }
  }

  async function importCurrentOriginal() {
    setBusy("upload");
    setMessage("");
    try {
      const response = await fetch(templateDownloadUrl(file.filePath));
      if (!response.ok) throw new Error("静态原件读取失败");
      const blob = await response.blob();
      await uploadVersion(new File([blob], file.fileName, { type: blob.type || "application/octet-stream" }));
    } catch (error) {
      setMessage(error.message || "导入原件失败");
      setBusy("");
    }
  }

  function addRole(type) {
    const role = createRole(type, config.roles.length);
    setConfig((current) => ({ ...current, roles: [...current.roles, role] }));
    setPlacementRoleId(role.id);
    setConfigDirty(true);
    setMessage(`已添加“${role.label}”，请在左侧${signatureSurfaceName}的目标位置点击放置`);
  }

  function updateRole(roleId, patch) {
    setConfig((current) => ({
      ...current,
      roles: current.roles.map((role) => role.id === roleId ? { ...role, ...patch } : role),
      fields: current.fields.map((field) => {
        if (field.roleId !== roleId || !patch.action) return field;
        const size = DEFAULT_FIELD_SIZE[patch.action];
        return {
          ...field,
          action: patch.action,
          width: size.width,
          height: size.height,
          x: Math.min(field.x, 100 - size.width),
          y: Math.min(field.y, 100 - size.height),
        };
      }),
    }));
    setConfigDirty(true);
  }

  async function updateElectronicSignature(required) {
    setBusy("settings");
    try {
      const saved = await saveTemplateVersionSettings(version.id, { requiresElectronicSignature: required });
      setVersion(saved);
      setMessage(required ? "已设为电子签模板，请配置并确认至少一个签署位置" : "已设为普通模板，可直接发布");
    } catch (error) {
      setMessage(error.message || "模板设置保存失败");
    } finally {
      setBusy("");
    }
  }

  async function downloadSource() {
    setBusy("download");
    try {
      const blob = await fetchTemplateVersionFile(version.id, "source");
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = version.sourceFileName || file.fileName || "template";
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      setMessage(error.message || "原件下载失败");
    } finally {
      setBusy("");
    }
  }

  async function downloadDocx() {
    setBusy("download");
    try {
      const blob = await fetchTemplateVersionFile(version.id, "docx");
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${file.displayName || cleanName(file.fileName)}.docx`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      setMessage(error.message || "DOCX 导出失败");
    } finally {
      setBusy("");
    }
  }

  async function saveContent() {
    const refreshOnly = !contentDirty && version.status !== "published";
    const draftRefreshOnly = !contentDirty && version.status === "published";
    if (!contentDirty && !refreshOnly && !draftRefreshOnly) {
      setMessage("正文没有待保存的修改；编辑内容后可直接点击保存");
      return;
    }
    const content = contentEditorRef.current?.getContent() || {
      documentJson: version.documentJson,
      editableHtml: version.editableHtml || "",
    };
    if (!content?.editableHtml) return;
    setBusy("content");
    setMessage(draftRefreshOnly ? "正在保存为新草稿并刷新版式" : refreshOnly ? "正在按当前正文刷新版式" : "");
    try {
      let targetVersion = version;
      // 已发布版保持不可变，正文修改必须先派生新草稿。
      if (version.status === "published") targetVersion = await createTemplateDraft(managed.id);
      const saved = await saveTemplateVersionContent(targetVersion.id, {
        ...content,
        pageCount: Math.max(1, Number(contentPageCount) || Number(version.pageCount) || 1),
        lockVersion: Number(targetVersion.lockVersion || 0),
      });
      const versions = (await getTemplateVersions(managed.id)).versions || [];
      await activateVersion({ ...managed, versions }, saved);
      setActiveTab("content");
      setMessage(version.status === "published"
        ? `已保存为新草稿 V${saved.versionNo}，版式已刷新，签章位置需要重新确认`
        : refreshOnly
          ? "版式预览已按当前正文刷新，签章位置需要重新确认"
          : "正文和版式预览已更新，签章位置需要重新确认");
      onSaved?.(managed.id);
    } catch (error) {
      setMessage(error.status === 409 ? "正文已被其他人更新，请关闭后重新打开" : error.message || "正文保存失败");
    } finally {
      setBusy("");
    }
  }

  function openContentTab() {
    setActiveTab("content");
  }

  function openSignatureTab() {
    if (contentDirty) {
      setActiveTab("content");
      setContentView("editor");
      setMessage("正文有未保存修改，请先点击“保存正文”，保存后预览和签章会使用同一版式");
      window.requestAnimationFrame(() => contentEditorRef.current?.focus?.());
      return;
    }
    setActiveTab("signature");
  }

  async function importLatestSource() {
    setBusy("source");
    setMessage("");
    try {
      const imported = await importSourceTemplate(file.sourceRecordId, {
        requiresElectronicSignature: version.requiresElectronicSignature,
      });
      const versions = (await getTemplateVersions(imported.templateId)).versions || [];
      await activateVersion({ id: imported.templateId, versions }, imported);
      setMessage(`数据库源更新已导入为草稿 V${imported.versionNo}`);
      onSaved?.(imported.templateId);
    } catch (error) {
      setMessage(error.message || "源更新导入失败");
    } finally {
      setBusy("");
    }
  }

  function deleteRole(roleId) {
    setConfig((current) => ({
      ...current,
      roles: current.roles.filter((role) => role.id !== roleId),
      fields: current.fields.filter((field) => field.roleId !== roleId),
    }));
    setConfigDirty(true);
  }

  function deleteField(fieldId) {
    setConfig((current) => ({ ...current, fields: current.fields.filter((field) => field.id !== fieldId) }));
    setSelectedFieldId((current) => current === fieldId ? "" : current);
    setConfigDirty(true);
  }

  function updateField(fieldId, patch) {
    setConfig((current) => ({
      ...current,
      fields: current.fields.map((field) => field.id === fieldId ? { ...field, ...patch } : field),
    }));
    setConfigDirty(true);
  }

  function addField(roleId, page, pointerX = 50, pointerY = 50, suggestion = false) {
    const role = config.roles.find((item) => item.id === roleId);
    if (!role) return;
    const pageNumber = Math.max(1, Math.round(Number(page) || 1));
    const size = DEFAULT_FIELD_SIZE[role.action] || DEFAULT_FIELD_SIZE["personal-signature"];
    const field = {
      id: crypto.randomUUID(),
      roleId,
      action: role.action,
      page: pageNumber,
      x: Math.max(0, Math.min(100 - size.width, pointerX - size.width / 2)),
      y: Math.max(0, Math.min(100 - size.height, pointerY - size.height / 2)),
      width: size.width,
      height: size.height,
      anchor: suggestion ? "ai-suggestion" : "manual",
      required: true,
      suggestion,
    };
    setConfig((current) => ({
      ...current,
      pageCount: Math.max(Number(current.pageCount) || 1, pageNumber),
      fields: [...current.fields, field],
    }));
    setSelectedFieldId(field.id);
    setPlacementRoleId("");
    setConfigDirty(true);
  }

  function smartSuggestions() {
    if (!config.roles.length) {
      setMessage("请先添加签署方，再生成智能建议");
      return;
    }
    const suggestions = config.roles.map((role, index) => {
      const size = DEFAULT_FIELD_SIZE[role.action];
      return {
        id: crypto.randomUUID(),
        roleId: role.id,
        action: role.action,
        page: version.editorFormat === "fixed" ? config.pageCount : editorPageCount,
        x: 12 + index * 42,
        y: 82,
        width: size.width,
        height: size.height,
        anchor: "ai-suggestion",
        required: true,
        suggestion: true,
      };
    });
    setConfig((current) => ({
      ...current,
      pageCount: Math.max(Number(current.pageCount) || 1, ...suggestions.map((field) => Number(field.page) || 1)),
      fields: [...current.fields, ...suggestions],
    }));
    setConfigDirty(true);
    setMessage("智能建议以虚线显示，原有正式字段未移动；请调整或删除后再确认");
  }

  function acceptSuggestions() {
    setConfig((current) => ({
      ...current,
      fields: current.fields.map((field) => field.suggestion ? { ...field, suggestion: false, anchor: "manual" } : field),
    }));
    setConfigDirty(true);
  }

  async function confirmConfig() {
    // AI 虚线建议只用于辅助定位，未点击接受前不能进入正式签章配置。
    const confirmedFields = config.fields.filter((field) => !field.suggestion).map(({ suggestion, action, ...field }) => field);
    if (contentPaginationPending) {
      setActiveTab("content");
      setContentView("preview");
      setMessage("正在计算正文分页，请稍后再保存签章位置");
      return;
    }
    if (!confirmedFields.length) {
      setMessage("电子签模板至少需要一个已确认的签署位置");
      return;
    }
    if (!configDirty) {
      setMessage("签章配置没有待保存的修改");
      return;
    }
    setBusy("save");
    try {
      let targetVersion = version;
      let nextManaged = managed;
      if (version.status === "published") {
        targetVersion = await createTemplateDraft(managed.id);
        nextManaged = { ...managed, versions: (await getTemplateVersions(managed.id)).versions || [] };
      }
      const confirmedPageCount = version.editorFormat === "fixed"
        ? Number(targetVersion.pageCount) || config.pageCount
        : Math.max(Number(editorPageCount) || 1, ...confirmedFields.map((field) => Number(field.page) || 1));
      const confirmed = {
        ...config,
        sourceSha256: targetVersion.renderedPdfSha256 || targetVersion.sourceFileSha256 || config.sourceSha256,
        pageCount: confirmedPageCount,
        fields: confirmedFields,
      };
      const saved = await saveVersionSignatureConfig(targetVersion.id, confirmed);
      if (version.status === "published") {
        await activateVersion(nextManaged, saved);
        setMessage(`签章位置已保存到新草稿 V${saved.versionNo}，原发布版本未改动`);
      } else {
        setVersion(saved);
        setConfig(normalizedConfig(saved));
        setSelectedFieldId("");
        setConfigDirty(false);
        setMessage("签章配置已确认并保存到当前草稿版本");
      }
      onSaved?.(managed?.id);
    } catch (error) {
      setMessage(error.message || "签章配置保存失败");
    } finally {
      setBusy("");
    }
  }

  async function publish() {
    // 发布动作必须以已落库的正文和签章配置为准，避免把界面中的临时修改误当成正式版本。
    if (contentDirty) {
      setActiveTab("content");
      setMessage("正文有未保存的修改，请先保存正文后再发布");
      return;
    }
    if (configDirty) {
      setActiveTab("signature");
      setMessage("签章位置有未保存的修改，请先保存签章配置后再发布");
      return;
    }
    if (version.requiresElectronicSignature && version.signatureConfigStatus !== "confirmed") {
      setActiveTab("signature");
      setMessage(`请先添加签署方，在${signatureSurfaceName}中放置签章位置并保存配置，然后再发布`);
      return;
    }
    setBusy("publish");
    try {
      const published = await publishTemplateVersion(version.id);
      setVersion(published);
      setManaged((current) => ({
        ...current,
        publishedVersion: published,
        versions: (current?.versions || []).map((item) => (
          item.id === published.id ? { ...item, ...published } : item
        )),
      }));
      setContentPageCount(Number(published.pageCount) || 0);
      if (published.editorFormat !== "fixed") {
        setActiveTab("content");
        setContentView("preview");
      }
      setMessage("版本已发布，业务发起将继承本版本的签章配置");
      onSaved?.(managed?.id);
    } catch (error) {
      setMessage(error.message || "发布失败");
    } finally {
      setBusy("");
    }
  }

  useEffect(() => {
    function handleDeleteKey(event) {
      if (!selectedFieldId || !config || !version?.requiresElectronicSignature) return;
      const target = event.target;
      if (target instanceof HTMLElement && (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))) return;
      if (event.key !== "Delete" && event.key !== "Backspace") return;
      event.preventDefault();
      deleteField(selectedFieldId);
    }
    window.addEventListener("keydown", handleDeleteKey);
    return () => window.removeEventListener("keydown", handleDeleteKey);
  }, [config, selectedFieldId, version?.requiresElectronicSignature]);

  if (status === "loading") return <div className="template-workbench-shell"><LoadingState label="正在读取模板版本" /></div>;

  if (status === "unmanaged") {
    return (
      <div className="template-workbench-shell">
        <header className="template-workbench-topbar"><button type="button" className="icon-button" onClick={onClose}><X size={18} /></button><div><strong>编辑模板</strong><span>{file.fileName}</span></div></header>
        <section className="template-import-state">
          <Upload size={28} />
          <h2>先创建可管理版本</h2>
          <p>当前文件仍由静态模板库提供。导入后会保存原件、PDF、文件哈希和不可变版本，不会修改静态原件。</p>
          <button type="button" className="primary-button" disabled={busy} onClick={importCurrentOriginal}><Upload size={16} />{busy ? "正在导入" : "导入当前原件"}</button>
          {message ? <p className="workbench-message is-error">{message}</p> : null}
        </section>
      </div>
    );
  }

  if (status === "error") return <div className="template-workbench-shell"><header className="template-workbench-topbar"><button type="button" className="icon-button" onClick={onClose}><X size={18} /></button></header><div className="template-import-state"><h2>模板读取失败</h2><p>{message}</p></div></div>;

  const confirmedCount = config.fields.filter((field) => !field.suggestion).length;
  const suggestionCount = config.fields.length - confirmedCount;
  const published = version.status === "published";
  const refreshDraftPdf = !contentDirty && !published;
  const savedPageCount = Number(version.pageCount) || 1;
  const editorPageCount = Number(contentPageCount) || savedPageCount;
  const contentPaginationPending = version.editorFormat !== "fixed" && !contentPageCount;
  const signatureUsesEditorPages = version.editorFormat !== "fixed";
  const signaturePlacementReady = signatureUsesEditorPages ? true : Boolean(pdf);
  const signatureSuggestionReady = signatureUsesEditorPages ? !contentPaginationPending : Boolean(pdf);
  const displayedPageCount = signatureUsesEditorPages ? editorPageCount : savedPageCount;
  const signatureSurfaceName = signatureUsesEditorPages ? "正文预览" : "PDF";
  // 同时检查摘要与版本历史，防止旧摘要误把过期发布版标记为当前版。
  const latestPublishedVersionNo = Math.max(
    Number(managed.publishedVersion?.versionNo || 0),
    ...(managed.versions || []).filter((item) => item.status === "published").map((item) => Number(item.versionNo || 0)),
  );
  const isCurrentPublishedVersion = published && Number(version.versionNo || 0) >= latestPublishedVersionNo;
  const requiresElectronicSignature = Boolean(version.requiresElectronicSignature);
  const canPublish = !configDirty && !contentDirty && !contentPaginationPending && (!requiresElectronicSignature || version.signatureConfigStatus === "confirmed");
  // 保留明确的中文阻断原因，按钮不可发布时用户无需猜测缺少哪一步。
  const publishBlockReason = contentDirty
    ? "正文有未保存的修改"
    : contentPaginationPending
      ? "正在计算正文分页"
    : configDirty
      ? "签章位置有未保存的修改"
      : requiresElectronicSignature && version.signatureConfigStatus !== "confirmed"
        ? "需要先配置并保存签章位置"
        : "";
  const selectedField = config.fields.find((field) => field.id === selectedFieldId);
  const selectedRole = selectedField ? config.roles.find((role) => role.id === selectedField.roleId) : null;
  const placementRole = placementRoleId ? config.roles.find((role) => role.id === placementRoleId) : null;

  return (
    <div className="template-workbench-shell">
      <header className="template-workbench-topbar">
        <button type="button" className="icon-button" onClick={onClose} title="关闭"><X size={18} /></button>
        <div className="template-workbench-title"><strong>{file.displayName || cleanName(file.fileName)}</strong><span>版本 {version.versionNo} · {published ? "已发布" : "草稿"} · {displayedPageCount} 页</span></div>
        <div className="template-workbench-actions">
          <div className="template-editor-tabs" role="tablist" aria-label="模板编辑模式">
            <button type="button" role="tab" aria-selected={activeTab === "content"} className={activeTab === "content" ? "is-active" : ""} disabled={version.editorFormat === "fixed"} onClick={openContentTab}><FileText size={15} />正文</button>
            <button type="button" role="tab" aria-selected={activeTab === "signature"} className={activeTab === "signature" ? "is-active" : ""} title={contentDirty ? "保存正文后再配置签章" : `在${signatureSurfaceName}上配置签章位置`} onClick={openSignatureTab}><Stamp size={15} />签章</button>
          </div>
          <span className={classNames("status-chip", published || configDirty || contentDirty ? "warning" : version.signatureConfigStatus === "confirmed" ? "success" : "warning")}>{published ? "发布版本" : configDirty || contentDirty ? "有未保存更改" : version.signatureConfigStatus === "confirmed" ? "签章已确认" : "草稿"}</span>
          {file.sourceUpdateAvailable ? <button type="button" className="secondary-button" disabled={busy} onClick={importLatestSource}><RefreshCw size={15} />导入源更新</button> : null}
          <button type="button" className="secondary-button" onClick={() => setVersionHistoryOpen((value) => !value)}><History size={15} />版本历史</button>
          <button type="button" className="secondary-button" disabled={busy} onClick={downloadSource}><Download size={15} />获取原件</button>
          {version.editorFormat !== "fixed" ? <button type="button" className="secondary-button" disabled={busy || contentDirty} onClick={downloadDocx}><Download size={15} />DOCX</button> : null}
          {activeTab === "content" ? <button type="button" className="primary-button" disabled={Boolean(busy)} title={contentDirty ? "保存正文修改并重新生成 PDF" : refreshDraftPdf ? "按当前编辑器分页重新生成版式 PDF" : "编辑内容后保存为新草稿"} onClick={saveContent}><Save size={16} />{published ? "保存为新草稿" : refreshDraftPdf ? "刷新版式 PDF" : "保存正文"}</button> : null}
          {!published ? <button type="button" className="primary-button" disabled={Boolean(busy)} title={canPublish ? "发布当前版本" : publishBlockReason} onClick={publish}><Check size={16} />发布版本</button> : null}
        </div>
      </header>

      <div className={classNames("template-workbench-grid", activeTab === "content" && "is-content-mode")}>
        <aside className="template-details-panel">
          <h2>模板资料</h2>
          <dl>
            <div className="template-classification-field"><dt><label htmlFor="templateClassificationModule">所属模块</label></dt><dd className="template-classification-control"><select id="templateClassificationModule" value={classification.moduleKey} disabled={busy} onChange={(event) => changeClassificationModule(event.target.value)}>{catalog.modules.map((item) => <option key={item.moduleKey} value={item.moduleKey}>{item.moduleName}</option>)}</select></dd></div>
            <div className="template-classification-field"><dt><label htmlFor="templateClassificationSubmodule">业务目录</label></dt><dd className="template-classification-control"><select id="templateClassificationSubmodule" value={classification.submoduleKey} disabled={busy || !classificationModule} onChange={(event) => setClassification((current) => ({ ...current, submoduleKey: event.target.value }))}>{(classificationModule?.submodules || []).map((item) => <option key={item.submoduleKey} value={item.submoduleKey}>{item.submoduleName}</option>)}</select><button type="button" className="secondary-button template-classification-save" disabled={busy || !classificationDirty || !classificationSubmodule} onClick={saveClassification}><Save size={14} />保存归类</button></dd></div>
            <div><dt>原文件</dt><dd>{version.sourceFileName}</dd></div>
            <div><dt>大小</dt><dd>{formatBytes(Number(version.sourceFileSize) || 0)}</dd></div>
            <div><dt>文件摘要</dt><dd className="hash-value">{version.sourceFileSha256}</dd></div>
          </dl>
          <label className="electronic-sign-toggle"><input type="checkbox" checked={requiresElectronicSignature} disabled={published || busy} onChange={(event) => updateElectronicSignature(event.target.checked)} /><span><strong>用于电子签署</strong><small>{requiresElectronicSignature ? "至少配置一个位置后才能发布" : "无需设置签章位置"}</small></span></label>
          {isCurrentPublishedVersion ? <button type="button" className="primary-button full-width" onClick={createDraftFromPublished} disabled={busy}><Copy size={16} />基于本版本创建新草稿</button> : null}
          <button type="button" className="secondary-button full-width" onClick={() => inputRef.current?.click()} disabled={busy}><Upload size={16} />{published ? "替换原文件并创建新草稿" : "替换原文件并创建新版本"}</button>
          <input ref={inputRef} type="file" accept={FILE_ACCEPT} hidden onChange={(event) => uploadVersion(event.target.files?.[0])} />
          {versionHistoryOpen ? <div className="version-history-list">{(managed.versions || []).map((item) => <button type="button" className={item.id === version.id ? "is-active" : ""} key={item.id} disabled={busy || item.id === version.id} onClick={() => openVersion(item)}><strong>V{item.versionNo}</strong><span>{item.status === "published" ? "已发布" : "草稿"}</span><small>{formatDate(item.createdAt)}</small></button>)}</div> : null}
        </aside>

        {activeTab === "content" ? (
          <main className="template-content-workspace">
            <div className="template-content-view-toolbar">
              <div className="template-content-view-tabs" role="tablist" aria-label="正文视图">
                <button type="button" role="tab" aria-selected={contentView === "preview"} className={contentView === "preview" ? "is-active" : ""} disabled={contentDirty} title={contentDirty ? "保存正文后查看版式预览" : "查看正文同源版式预览"} onClick={() => setContentView("preview")}><Eye size={15} />版式预览</button>
                <button type="button" role="tab" aria-selected={contentView === "editor"} className={contentView === "editor" ? "is-active" : ""} onClick={() => setContentView("editor")}><PencilLine size={15} />编辑正文</button>
              </div>
              <span>{contentView === "preview" ? "正文预览 · 与编辑分页一致" : contentDirty ? "正文有未保存修改" : "正文源稿"}</span>
            </div>
            {contentView === "editor" && published ? <div className="published-edit-notice"><Copy size={15} /><span>保存正文时会复制为新草稿，发布版本保持不变。</span></div> : null}
            <div className="template-content-editor-host">
              <TemplateContentEditor
                ref={contentEditorRef}
                version={version}
                templateTitle={file.displayName || cleanName(file.fileName)}
                readOnly={contentView === "preview" || Boolean(busy)}
                preview={contentView === "preview"}
                onDirtyChange={setContentDirty}
                onPageCountChange={setContentPageCount}
              />
            </div>
          </main>
        ) : (
          <main className="template-pdf-workspace">
            {published ? <div className="published-edit-notice"><Copy size={15} /><span>保存签章时会复制为新草稿，发布版本保持不变。</span></div> : null}
            <div className="pdf-workspace-toolbar"><span>{signatureUsesEditorPages ? "正文签章定位" : "发布 PDF"}</span><small>{placementRole ? `请在${signatureSurfaceName}页面点击放置“${placementRole.label}”` : `拖动已有位置调整；在右侧选择新增位置`}</small><button type="button" className="secondary-button" disabled={!requiresElectronicSignature || busy || !signatureSuggestionReady} onClick={smartSuggestions}><Sparkles size={15} />智能建议位置</button>{suggestionCount ? <button type="button" className="secondary-button" disabled={busy} onClick={acceptSuggestions}><Check size={15} />接受 {suggestionCount} 条建议</button> : null}</div>
            {signatureUsesEditorPages ? (
              <div className="signature-editor-scroll" data-version-id={version.id} data-layout-source="editor-preview" data-page-count={editorPageCount}>
                <div className="template-content-editor-host">
	                  <TemplateContentEditor
	                    ref={contentEditorRef}
	                    version={version}
	                    templateTitle={file.displayName || cleanName(file.fileName)}
	                    readOnly
	                    preview
                    onDirtyChange={setContentDirty}
                    onPageCountChange={setContentPageCount}
                    signatureOverlay={{
                      fields: config.fields,
                      roles: config.roles,
                      selectedFieldId,
                      placementRoleId,
                      onFieldSelect: setSelectedFieldId,
                      onFieldChange: updateField,
                      onFieldDelete: deleteField,
                      onDropRole: addField,
                      onPlaceRole: addField,
                      readOnly: !requiresElectronicSignature || Boolean(busy),
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className="signature-pdf-scroll" data-version-id={version.id} data-pdf-sha256={version.renderedPdfSha256 || ""}>
                {pdf ? Array.from({ length: pdf.numPages }, (_, index) => (
                  <PdfPage
                    key={index + 1}
                    pdf={pdf}
                    pageNumber={index + 1}
                    fields={config.fields.filter((field) => field.page === index + 1)}
                    roles={config.roles}
                    selectedFieldId={selectedFieldId}
                    placementRoleId={placementRoleId}
                    onFieldSelect={setSelectedFieldId}
                    onFieldChange={updateField}
                    onFieldDelete={deleteField}
                    onDropRole={(...args) => addField(...args)}
                    onPlaceRole={(...args) => addField(...args)}
                    readOnly={!requiresElectronicSignature || Boolean(busy)}
                  />
                )) : <LoadingState label="正在渲染 PDF" />}
              </div>
            )}
          </main>
        )}

        {activeTab === "signature" ? <aside className="signature-role-panel">
          <div className="signature-role-heading"><div><h2>签署方与字段</h2><p>{config.roles.length} 个签署方 · {confirmedCount} 个正式位置</p></div></div>
          <div className="role-add-menu">
            <button type="button" disabled={busy || !requiresElectronicSignature} onClick={() => addRole("company")}><Building2 size={15} />企业方</button>
            <button type="button" disabled={busy || !requiresElectronicSignature} onClick={() => addRole("employee")}><UserRound size={15} />员工方</button>
            <button type="button" disabled={busy || !requiresElectronicSignature} onClick={() => addRole("other")}><UsersRound size={15} />其他方</button>
          </div>
          {selectedField ? <div className="selected-signature-field"><div><strong>{selectedRole?.label || "签署方"}</strong><span>第 {selectedField.page} 页 · 已选中，可直接拖动</span></div><button type="button" className="danger-text-button" disabled={busy} onClick={() => deleteField(selectedField.id)}><Trash2 size={14} />删除选中位置</button></div> : null}
          <div className="signature-role-list">
            {config.roles.map((role) => {
              const count = config.fields.filter((field) => field.roleId === role.id && !field.suggestion).length;
              return (
                <article className={classNames("signature-role-item", placementRoleId === role.id && "is-placing")} key={role.id}>
                  <header><span className="role-drag-handle" draggable={!busy && requiresElectronicSignature} title={`拖到${signatureSurfaceName}新增位置`} onDragStart={(event) => { event.dataTransfer.setData("application/x-signature-role", role.id); event.dataTransfer.effectAllowed = "copy"; }}><GripVertical size={16} /></span><input value={role.label} disabled={busy} onChange={(event) => updateRole(role.id, { label: event.target.value })} /><button type="button" title="删除签署方" disabled={busy} onClick={() => deleteRole(role.id)}><Trash2 size={14} /></button></header>
                  <label><span>签署动作</span><select value={role.action} disabled={busy} onChange={(event) => updateRole(role.id, { action: event.target.value })}><option value="company-seal">公司盖章</option><option value="personal-signature">个人签名</option></select></label>
                  <label><span>变量绑定</span><select value={role.variableBinding} disabled={busy} onChange={(event) => updateRole(role.id, { variableBinding: event.target.value })}><option value="">不绑定</option><option value="companyName">当前公司</option><option value="staffName">当前员工</option></select></label>
                  <footer><span>{count} 处正式位置</span><button type="button" className={placementRoleId === role.id ? "is-active" : ""} disabled={busy || !signaturePlacementReady || !requiresElectronicSignature} onClick={() => setPlacementRoleId((current) => current === role.id ? "" : role.id)}><MousePointer2 size={13} />{placementRoleId === role.id ? "取消放置" : `在${signatureSurfaceName}中放置`}</button></footer>
                </article>
              );
            })}
            {!config.roles.length ? <div className="role-empty-state">先点击上方“企业方”或“员工方”，再到左侧{signatureSurfaceName}的目标位置点击，即可放置盖章或签名位置。</div> : null}
          </div>
          <div className="signature-config-actions">
            <span>{!requiresElectronicSignature ? "当前版本不用于电子签署" : suggestionCount ? `${suggestionCount} 条虚线建议尚未确认` : configDirty ? "有未保存的位置变更" : confirmedCount ? "签章位置已保存" : "尚未配置正式位置"}</span>
            <button type="button" className="primary-button full-width" disabled={Boolean(busy) || !requiresElectronicSignature} title={!confirmedCount ? `请先在${signatureSurfaceName}中放置至少一个签章位置` : !configDirty ? "签章配置没有待保存的修改" : "保存并确认签章配置"} onClick={confirmConfig}><Save size={16} />{published ? "保存为新草稿" : "保存签章配置"}</button>
          </div>
        </aside> : null}
      </div>
      {message ? <div className="workbench-message">{message}</div> : null}
    </div>
  );
}
